import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { codigoBarrasService, CodigoBarrasInformacoes } from '../../services/codigoBarras';

const CodigoBarrasTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Buscar todos os códigos de barras
  const { data: codigos = [], isLoading, error } = useQuery({
    queryKey: ['codigoBarras'],
    queryFn: codigoBarrasService.getAll,
  });

  // Filtrar códigos baseado na busca
  const filteredCodigos = useMemo(() => {
    return codigos.filter(codigo =>
      codigo.referenciaInformada.toLowerCase().includes(search.toLowerCase()) ||
      codigo.descricao.toLowerCase().includes(search.toLowerCase())
    );
  }, [codigos, search]);

  // Paginação dos dados filtrados
  const paginatedCodigos = useMemo(() => {
    return filteredCodigos.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredCodigos, page, rowsPerPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePrint = async (id: number) => {
    try {
      setSnackbar({
        open: true,
        message: 'Gerando arquivo para impressão. Aguarde!',
        severity: 'success',
      });

      const blob = await codigoBarrasService.imprimir(id);
      
      // Criar link para download do PDF
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nome do arquivo com data atual
      const now = new Date();
      const fileName = `Etiquetas_${now.toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
      link.download = fileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'PDF gerado com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao gerar PDF. Tente novamente.',
        severity: 'error',
      });
    }
  };

  const handleNew = () => {
    navigate('/codigoBarras/novo');
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar os códigos de barras.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            placeholder="Filtrar"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
            sx={{ minWidth: 300 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNew}
            sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
          >
            Gerar Código
          </Button>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCodigos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography sx={{ py: 4, color: 'text.secondary' }}>
                      {search ? 'Nenhum código encontrado para o filtro informado.' : 'Nenhum código de barras gerado.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCodigos.map((codigo) => (
                  <TableRow key={codigo.id} hover>
                    <TableCell>{codigo.id}</TableCell>
                    <TableCell>{codigo.referenciaInformada}</TableCell>
                    <TableCell>
                      {codigo.dataGeracao 
                        ? (() => {
                            // Converte data do formato brasileiro dd/mm/yyyy para Date
                            const [day, month, year] = codigo.dataGeracao.split('/');
                            const date = new Date(Number(year), Number(month) - 1, Number(day));
                            return date.toLocaleDateString('pt-BR');
                          })()
                        : 'N/A'
                      }
                    </TableCell>
                    <TableCell>{codigo.quantidade}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handlePrint(codigo.id)}
                        size="small"
                        title="Imprimir"
                      >
                        <PrintIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      
      {/* Paginação */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredCodigos.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CodigoBarrasTable;
